import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQueryClient } from 'react-query';
import { translate } from 'translations';
import { platform } from 'network-utils';
import { SelectLanguage } from 'learning-panel-components';
import { showAlert } from '@redux/slices/informationSlice';
import { selectUserUuid, setLocale } from '@redux/slices/userSlice';
import { useGetAccountData, getAccountData } from '@queries/platform/accountSettings';

const SelectLanguageContainer: FC<SelectLanguageContainerProps> = (props: SelectLanguageContainerProps) => {
    const { setShowSuccessNotification, setNotificationMessage } = props;
    const services = platform.learningPanel.AccountSettingsAPIs;
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const localeUpdated = translate('localeUpdated');
    const failedToUpdateLang = translate('FailedToChangeLang');
    const userUuid = useSelector(selectUserUuid);
    const { data } = useGetAccountData(userUuid);
    const accountData = data?.account_data;

    const updateLocale = async (input: { locale: string }): Promise<{ success: boolean; error?: string }> => {
        const updateLocaleResponse = await services.updateLocale(input);
        return updateLocaleResponse;
    };

    const showError = () => {
        dispatch(
            showAlert({
                value: failedToUpdateLang,
                status: 'error'
            })
        );
    };

    const { mutate } = useMutation(updateLocale, {
        onSuccess: (updateLocaleResponse, variables) => {
            if (updateLocaleResponse?.success) {
                queryClient.setQueryData([getAccountData, userUuid], oldData => ({
                    account_data: {
                        ...(oldData as platform.AccountData).account_data,
                        locale: variables.locale
                    }
                }));
                dispatch(setLocale(variables.locale));
                setShowSuccessNotification?.(true);
                setNotificationMessage?.(localeUpdated);
                return;
            }
            showError();
        },
        onError: () => {
            showError();
        }
    });

    return (
        <SelectLanguage
            userLocale={accountData?.locale || 'en'}
            setUserLocale={(locale: string) => mutate({ locale })}
        />
    );
};

export interface SelectLanguageContainerProps {
    setShowSuccessNotification: Function;
    setNotificationMessage: Function;
}
export default SelectLanguageContainer;
